import React, { useState } from 'react';
import { X, Users, Shield } from 'lucide-react';
import { collection, addDoc, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { FIRESTORE_PATHS } from '../../config/appConfig';
import { useToast } from '../../contexts/ToastContext';
import logger from '../../utils/logger';

const CreateTeamModal = ({ onClose, onTeamCreated, userId, userName }) => {
    const [teamName, setTeamName] = useState('');
    const [description, setDescription] = useState('');
    const [primaryColor, setPrimaryColor] = useState('#3B82F6');
    const [accentColor, setAccentColor] = useState('#8B5CF6');
    const [creating, setCreating] = useState(false);
    const { error: showError, info: showInfo } = useToast();

    const colorPresets = [
        { primary: '#3B82F6', accent: '#8B5CF6', name: 'Royal Blue' },
        { primary: '#EF4444', accent: '#F59E0B', name: 'Fire Red' },
        { primary: '#10B981', accent: '#06B6D4', name: 'Emerald' },
        { primary: '#F59E0B', accent: '#EF4444', name: 'Sunrisers' },
        { primary: '#8B5CF6', accent: '#EC4899', name: 'Purple Storm' },
        { primary: '#1F2937', accent: '#6B7280', name: 'Steel Gray' },
    ];

    const handleCreate = async () => {
        if (!teamName.trim()) {
            showError('Please enter a team name');
            return;
        }

        if (!userId) {
            showError('User ID is missing. Please log in again.');
            return;
        }

        setCreating(true);
        let createdTeamId = null;

        try {
            // 1. Create team document
            // alert('Creating team document...');
            const teamDoc = await addDoc(collection(db, FIRESTORE_PATHS.TEAMS), {
                meta: {
                    name: teamName.trim(),
                    description: description.trim(),
                    theme: {
                        primary: primaryColor,
                        accent: accentColor
                    }
                },
                roster: {
                    [userId]: { role: 'captain', status: 'active', name: userName }
                },
                createdAt: serverTimestamp(),
                createdBy: userId
            });
            createdTeamId = teamDoc.id;
            // alert(`Team created! ID: ${createdTeamId}`);

            // 2. Add team to user's Teams subcollection (organized structure)
            try {
                await setDoc(doc(db, FIRESTORE_PATHS.USERS, userId, FIRESTORE_PATHS.TEAMS, teamDoc.id), {
                    teamId: teamDoc.id,
                    teamName: teamName.trim(),
                    joinedAt: serverTimestamp()
                });
            } catch (profileErr) {
                logger.error('CreateTeam', 'Error linking team to user:', profileErr);
                showInfo(`Team created but failed to link to profile: ${profileErr.message}`);
                // Proceed anyway as team exists
            }

            // 3. Refresh list
            if (onTeamCreated) {
                try {
                    await onTeamCreated(teamDoc.id);
                } catch (refreshErr) {
                    logger.error('CreateTeam', 'Error refreshing list:', refreshErr);
                    // alert("Team created but list refresh failed.");
                }
            }

            // 4. Success & Close
            // alert("Success! Closing modal.");
            onClose();
        } catch (err) {
            logger.error('CreateTeam', 'Error creating team:', err);
            showError(`Failed to create team: ${err.message}`);
            setCreating(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/90 z-50 flex flex-col">
            {/* Header */}
            <div className="p-4 bg-gray-800 flex items-center justify-between border-b border-gray-700">
                <h2 className="text-white font-bold">Create Your Team</h2>
                <button onClick={onClose} className="text-gray-400 hover:text-white">
                    <X size={20} />
                </button>
            </div>

            {/* Form */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
                {/* Team Name */}
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                        Team Name *
                    </label>
                    <input
                        type="text"
                        value={teamName}
                        onChange={(e) => setTeamName(e.target.value)}
                        placeholder="e.g., Mumbai Indians XI"
                        maxLength={30}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-neon-cyan"
                    />
                </div>

                {/* Description */}
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                        Description
                    </label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Tell others about your team..."
                        rows={3}
                        maxLength={150}
                        className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-neon-cyan resize-none"
                    />
                </div>

                {/* Color Theme */}
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                        Team Colors
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                        {colorPresets.map((preset, idx) => (
                            <button
                                key={idx}
                                onClick={() => {
                                    setPrimaryColor(preset.primary);
                                    setAccentColor(preset.accent);
                                }}
                                className={`p-3 rounded-lg border-2 transition ${primaryColor === preset.primary && accentColor === preset.accent
                                    ? 'border-neon-cyan'
                                    : 'border-gray-700 hover:border-gray-600'
                                    }`}
                            >
                                <div
                                    className="w-full h-8 rounded-md mb-2"
                                    style={{ background: `linear-gradient(135deg, ${preset.primary}, ${preset.accent})` }}
                                />
                                <span className="text-[10px] text-gray-400">{preset.name}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Preview */}
                <div>
                    <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
                        Preview
                    </label>
                    <div
                        className="p-4 rounded-xl border border-gray-700"
                        style={{ borderLeftColor: primaryColor, borderLeftWidth: '4px' }}
                    >
                        <div className="flex items-center gap-4">
                            <div
                                className="w-14 h-14 rounded-lg flex items-center justify-center font-bold text-xl text-white"
                                style={{ background: `linear-gradient(135deg, ${primaryColor}, ${accentColor})` }}
                            >
                                {teamName?.charAt(0)?.toUpperCase() || 'T'}
                            </div>
                            <div className="flex-1">
                                <h4 className="text-white font-bold">{teamName || 'Team Name'}</h4>
                                <span className="text-xs text-gray-500"><Users size={10} className="inline" /> 1 member (You)</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="p-4 bg-gray-800 border-t border-gray-700">
                <button
                    onClick={handleCreate}
                    disabled={creating || !teamName.trim()}
                    className={`w-full py-3 rounded-xl font-bold text-black transition flex items-center justify-center gap-2 ${creating || !teamName.trim()
                        ? 'bg-gray-600 cursor-not-allowed'
                        : 'bg-neon-cyan hover:bg-cyan-400'
                        }`}
                >
                    {creating ? (
                        <>
                            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                            Creating...
                        </>
                    ) : (
                        <>
                            <Shield size={18} />
                            Create Team
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default CreateTeamModal;
